/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  Users,
  UserPlus,
  UserCheck,
  UserX,
  Search,
  Sparkles,
  Check,
  X,
  ArrowUpDown,
  Award,
  BookOpen,
  ArrowLeft,
  Briefcase,
  HelpCircle,
  Clock,
  Trash2,
  RefreshCw,
  Plus
} from 'lucide-react';
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  setDoc,
  deleteDoc,
  onSnapshot,
  limit
} from 'firebase/firestore';
import { db, OperationType, handleFirestoreError, isFirestoreQuotaError, notifyQuotaExceeded } from '../firebase';
import { Avatar } from './Avatar';
import { TradeBoard } from './TradeBoard';
import { ALL_STICKERS, STICKER_MAP } from '../constants';
import { LanguageCode, TRANSLATIONS } from '../translations';

interface FriendsPageProps {
  user: any;
  owned: Set<string>;
  duplicates: string[];
  onBack: () => void;
  onUpdateInventory?: (owned: Set<string>, duplicates: string[]) => Promise<void>;
  defaultTab?: 'social' | 'trade';
  lang?: string;
}

interface FriendProfile {
  userId: string;
  email: string;
  displayName?: string;
  photoURL?: string;
  owned?: string[];
  duplicates?: string[];
  packs?: number;
  connectOpen?: boolean;
}

interface Friendship {
  id: string;
  users: string[];
  status: 'pending' | 'accepted';
  senderId: string;
  receiverId: string;
  createdAt: string;
  updatedAt: string;
}

export const FriendsPage = ({ user, owned, duplicates, onBack, onUpdateInventory, defaultTab = 'social', lang }: FriendsPageProps) => {
  const getLang = (): LanguageCode => {
    if (lang && lang in TRANSLATIONS) return lang as LanguageCode;
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('paganini-lang');
      if (saved && saved in TRANSLATIONS) return saved as LanguageCode;
    }
    return 'en';
  };
  const activeLang = getLang();
  const t = (key: string) => TRANSLATIONS[activeLang]?.[key] || TRANSLATIONS['en']?.[key] || key;

  const [friendships, setFriendships] = useState<Friendship[]>([]);
  const [friendProfiles, setFriendProfiles] = useState<Record<string, FriendProfile>>({});
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<FriendProfile[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchMessage, setSearchMessage] = useState<string | null>(null);

  // Tab controller for the main page
  const [activeTab, setActiveTab] = useState<'social' | 'trade'>(defaultTab);

  useEffect(() => {
    if (defaultTab) {
      setActiveTab(defaultTab);
    }
  }, [defaultTab]);

  // Trade board states
  const [trades, setTrades] = useState<any[]>([]);
  const [selectedOfferStickerId, setSelectedOfferStickerId] = useState<string>('');
  const [selectedWantStickerId, setSelectedWantStickerId] = useState<string>('');
  const [tradeMessage, setTradeMessage] = useState<{ text: string; type: 'success' | 'err' } | null>(null);
  const [tradeActionLoading, setTradeActionLoading] = useState<string | null>(null);

  // Community directory profiles (to resolve empty slate in local preview)
  const [directoryUsers, setDirectoryUsers] = useState<FriendProfile[]>([]);
  const [loadingDirectory, setLoadingDirectory] = useState(false);

  // Compare Album overlay section
  const [selectedFriendCompare, setSelectedFriendCompare] = useState<FriendProfile | null>(null);
  const [compareTab, setCompareTab] = useState<'summary' | 'get' | 'give'>('summary');

  const myUid = user?.uid;

  // Real-time listener to fetch and distribute active trades
  useEffect(() => {
    if (!myUid) return;
    const path = 'trades';
    const q = query(
        collection(db, 'trades')
    );

    const unsubscribe = onSnapshot(
        q,
        (snapshot) => {
          const list: any[] = [];
          snapshot.forEach((doc) => {
            list.push({ id: doc.id, ...doc.data() });
          });
          setTrades(list);
        },
        (error) => {
          console.error("Failed to load real-time trades in snapshot: ", error);
          if (isFirestoreQuotaError(error)) {
            notifyQuotaExceeded();
          } else {
            handleFirestoreError(error, OperationType.LIST, path);
          }
        }
    );

    return () => unsubscribe();
  }, [myUid]);

  // Real-time listener for current user's friendships
  useEffect(() => {
    if (!myUid) return;

    const path = 'friendships';
    const q = query(
        collection(db, 'friendships'),
        where('users', 'array-contains', myUid)
    );

    const unsubscribe = onSnapshot(
        q,
        async (snapshot) => {
          const list: Friendship[] = [];
          const uidsToFetch = new Set<string>();

          snapshot.forEach((doc) => {
            const data = doc.data() as Friendship;
            list.push(data);
            // Find the other user's UID
            const otherUid = data.users.find(id => id !== myUid);
            if (otherUid) {
              uidsToFetch.add(otherUid);
            }
          });

          setFriendships(list);

          // Fetch profiles of these users in real-time, matching cached values if needed
          for (const uid of uidsToFetch) {
            fetchAndCacheProfile(uid);
          }
        },
        (error) => {
          console.error("Failed to load real-time friendships in snapshot: ", error);
          if (isFirestoreQuotaError(error)) {
            notifyQuotaExceeded();
          } else {
            handleFirestoreError(error, OperationType.LIST, path);
          }
        }
    );

    return () => unsubscribe();
  }, [myUid]);

  // Load community directory on mount to help people connect
  useEffect(() => {
    loadCommunityDirectory();
  }, [myUid]);

  // Fetch a user's full public Profile
  const fetchAndCacheProfile = async (uid: string) => {
    try {
      const userRef = doc(db, 'users', uid);
      const userSnap = await getDocs(query(collection(db, 'users'), where('userId', '==', uid)));
      if (!userSnap.empty) {
        const uDoc = userSnap.docs[0];
        const profile = uDoc.data() as FriendProfile;
        setFriendProfiles(prev => ({
          ...prev,
          [uid]: profile
        }));
        return profile;
      }
    } catch (err) {
      console.error("Could not fetch profile for friend ID: ", uid, err);
    }
    return null;
  };

  // Directory users: first of all load players looking to connect (connectOpen == true) and fallback to top 15 list
  const loadCommunityDirectory = async () => {
    setLoadingDirectory(true);
    try {
      const q = query(
          collection(db, 'users'),
          where('connectOpen', '==', true),
          limit(30)
      );
      const querySnap = await getDocs(q);
      const list: FriendProfile[] = [];
      querySnap.forEach((doc) => {
        const data = doc.data() as FriendProfile;
        if (data.userId !== myUid) {
          list.push(data);
        }
      });

      // If no users have turned on their connect flag yet, show registered users as a graceful fallback/directory base
      if (list.length === 0) {
        const qFallback = query(collection(db, 'users'), limit(15));
        const fallbackSnap = await getDocs(qFallback);
        fallbackSnap.forEach((doc) => {
          const data = doc.data() as FriendProfile;
          if (data.userId !== myUid) {
            list.push(data);
          }
        });
      }
      setDirectoryUsers(list);
    } catch (err) {
      console.error("Directory fetching failed: ", err);
    } finally {
      setLoadingDirectory(false);
    }
  };

  // Perform a manual search for users by exact email or nickname
  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    const queryTerm = searchQuery.trim();
    if (!queryTerm) {
      setSearchMessage("Please provide an email address or nickname to locate.");
      setSearchResults([]);
      return;
    }

    setSearchLoading(true);
    setSearchMessage(null);
    setSearchResults([]);

    try {
      // 1. Search by exact email match
      const qEmail = query(
          collection(db, 'users'),
          where('email', '==', queryTerm.toLowerCase())
      );
      const emailSnap = await getDocs(qEmail);

      let found: FriendProfile[] = [];
      emailSnap.forEach((doc) => {
        const data = doc.data() as FriendProfile;
        if (data.userId !== myUid) {
          found.push(data);
        }
      });

      // 2. Fallback or Additional check: Search by exact nickname
      if (found.length === 0) {
        const qNick = query(
            collection(db, 'users'),
            where('displayName', '==', queryTerm)
        );
        const nickSnap = await getDocs(qNick);
        nickSnap.forEach((doc) => {
          const data = doc.data() as FriendProfile;
          if (data.userId !== myUid && !found.some(item => item.userId === data.userId)) {
            found.push(data);
          }
        });
      }

      setSearchResults(found);
      if (found.length === 0) {
        setSearchMessage(`No collector matches the term "${queryTerm}". Make sure they have a registered cloud safe!`);
      }
    } catch (err: any) {
      console.error("Search error: ", err);
      setSearchMessage("Network error querying database. Try again shortly.");
    } finally {
      setSearchLoading(false);
    }
  };

  // Initiate an outbound Friendship request
  const sendFriendRequest = async (receiver: FriendProfile) => {
    if (!myUid || !receiver.userId) return;
    const path = 'friendships';

    // Sort UIDs to ensure a unique friendship identifier
    const friendshipId = [myUid, receiver.userId].sort().join('_');

    const newRequest: Friendship = {
      id: friendshipId,
      users: [myUid, receiver.userId],
      status: 'pending',
      senderId: myUid,
      receiverId: receiver.userId,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    try {
      await setDoc(doc(db, 'friendships', friendshipId), newRequest);
      // Immediately pull fresh directory listing or search status
      setFriendships(prev => [...prev.filter(f => f.id !== friendshipId), newRequest]);
    } catch (err: any) {
      console.error("Failed to post friend request: ", err);
      handleFirestoreError(err, OperationType.WRITE, `${path}/${friendshipId}`);
    }
  };

  // Accept an inbound Friendship request
  const acceptFriendRequest = async (friendship: Friendship) => {
    const path = 'friendships';
    const updated: Friendship = {
      ...friendship,
      status: 'accepted',
      updatedAt: new Date().toISOString()
    };

    try {
      await setDoc(doc(db, 'friendships', friendship.id), updated);
      // Force refresh other collector profile cache
      const otherUid = friendship.users.find(id => id !== myUid);
      if (otherUid) {
        fetchAndCacheProfile(otherUid);
      }
    } catch (err: any) {
      console.error("Failed to accept friendship request: ", err);
      handleFirestoreError(err, OperationType.WRITE, `${path}/${friendship.id}`);
    }
  };

  // Reject / Remove friendship connection
  const terminateFriendship = async (friendshipId: string) => {
    const path = 'friendships';
    try {
      await deleteDoc(doc(db, 'friendships', friendshipId));
      if (selectedFriendCompare && [myUid, selectedFriendCompare.userId].sort().join('_') === friendshipId) {
        setSelectedFriendCompare(null);
      }
    } catch (err: any) {
      console.error("Failed to remove friendship request/connection: ", err);
      handleFirestoreError(err, OperationType.DELETE, `${path}/${friendshipId}`);
    }
  };

  // Get current status helper
  const getConnectionState = (otherUid: string) => {
    const match = friendships.find(f => f.users.includes(otherUid));
    if (!match) return 'none';
    if (match.status === 'accepted') return 'accepted';
    return match.senderId === myUid ? 'sent_pending' : 'received_pending';
  };

  const getFriendshipObject = (otherUid: string) => {
    return friendships.find(f => f.users.includes(otherUid));
  };

  // Split into lists for beautiful tab outputs
  const acceptedFriends = friendships
      .filter(f => f.status === 'accepted')
      .map(f => {
        const otherId = f.users.find(id => id !== myUid)!;
        return friendProfiles[otherId] || { userId: otherId, email: 'Loading...', displayName: 'Loading...' };
      });

  const inboundPending = friendships.filter(f => f.status === 'pending' && f.receiverId === myUid);
  const outboundPending = friendships.filter(f => f.status === 'pending' && f.senderId === myUid);

  // Sticker trading matrix computation
  const getTradingMatrix = (friend: FriendProfile) => {
    const fOwned = new Set(friend.owned || []);
    const fDuplicates = friend.duplicates || [];

    // Duplicates the friend has, which I do not own
    const duplicatesYouNeed = fDuplicates
        .filter(id => !owned.has(id))
        .map(id => STICKER_MAP[id])
        .filter(Boolean);

    // Duplicates I have, which the friend does not own
    const duplicatesFriendNeeds = duplicates
        .filter(id => !fOwned.has(id))
        .map(id => STICKER_MAP[id])
        .filter(Boolean);

    // Filter duplicates to make it unique lists
    const uniqueDuplicatesYouNeed = Array.from(new Set(duplicatesYouNeed.map(s => s.id)))
        .map(id => STICKER_MAP[id]);

    const uniqueDuplicatesFriendNeeds = Array.from(new Set(duplicatesFriendNeeds.map(s => s.id)))
        .map(id => STICKER_MAP[id]);

    return {
      youNeed: uniqueDuplicatesYouNeed,
      friendNeeds: uniqueDuplicatesFriendNeeds
    };
  };

  // Trade actions
  const handleCreateTradeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedOfferStickerId || !selectedWantStickerId) {
      setTradeMessage({ text: "Select an offered duplicate and a missing card to proceed.", type: 'err' });
      return;
    }

    if (!duplicates.includes(selectedOfferStickerId)) {
      setTradeMessage({ text: "You don't own this duplicate anymore.", type: 'err' });
      return;
    }

    setTradeActionLoading('create');
    setTradeMessage(null);
    try {
      const id = `trade_${myUid}_${Date.now()}`;
      await setDoc(doc(db, 'trades', id), {
        id,
        userId: myUid,
        userName: user?.displayName || user?.email?.split('@')[0] || 'Collector',
        userEmail: user?.email || '',
        offeredStickerId: selectedOfferStickerId,
        wantedStickerId: selectedWantStickerId,
        status: 'pending',
        createdAt: new Date().toISOString()
      });
      setTradeMessage({ text: "Successfully posted your sticker trade request!", type: 'success' });
      setSelectedOfferStickerId('');
      setSelectedWantStickerId('');
    } catch (err) {
      console.error("Failed to post trade:", err);
      setTradeMessage({ text: "Database connection failed.", type: 'err' });
    } finally {
      setTradeActionLoading(null);
    }
  };

  const handleCancelTrade = async (tradeId: string) => {
    setTradeActionLoading(tradeId);
    setTradeMessage(null);
    try {
      await deleteDoc(doc(db, 'trades', tradeId));
      setTradeMessage({ text: "Trade offer removed successfully.", type: 'success' });
    } catch (err) {
      console.error("Failed to cancel trade:", err);
      setTradeMessage({ text: "Failed to remove offer from database.", type: 'err' });
    } finally {
      setTradeActionLoading(null);
    }
  };

  const handleAcceptTrade = async (trade: any) => {
    if (!onUpdateInventory) {
      setTradeMessage({ text: "Local state sync is offline.", type: 'err' });
      return;
    }
    const { id, offeredStickerId, wantedStickerId, userId, userName } = trade;

    if (!duplicates.includes(wantedStickerId)) {
      setTradeMessage({
        text: `You must own a duplicate of ${STICKER_MAP[wantedStickerId]?.name || wantedStickerId} to fulfill this trade.`,
        type: 'err'
      });
      return;
    }

    setTradeActionLoading(id);
    setTradeMessage(null);

    try {
      const nextOwned = new Set(owned);
      nextOwned.add(offeredStickerId);

      const nextDuplicates = [...duplicates];
      const idx = nextDuplicates.indexOf(wantedStickerId);
      if (idx > -1) {
        nextDuplicates.splice(idx, 1);
      }

      await setDoc(doc(db, 'trades', id), {
        ...trade,
        status: 'completed',
        partnerId: myUid,
        partnerName: user?.displayName || user?.email?.split('@')[0] || 'Collector',
        partnerEmail: user?.email || '',
        updatedAt: new Date().toISOString()
      });

      await onUpdateInventory(nextOwned, nextDuplicates);

      setTradeMessage({
        text: `Exchange Complete! You traded ${STICKER_MAP[wantedStickerId]?.name} for ${STICKER_MAP[offeredStickerId]?.name} with ${userName}!`,
        type: 'success'
      });
    } catch (err) {
      console.error("Trade execution error:", err);
      setTradeMessage({ text: "Transaction error. Database is locked.", type: 'err' });
    } finally {
      setTradeActionLoading(null);
    }
  };

  const handleClaimCompletedTrade = async (trade: any) => {
    if (!onUpdateInventory) return;
    const { id, offeredStickerId, wantedStickerId, partnerName } = trade;

    setTradeActionLoading(`claim_${id}`);
    setTradeMessage(null);

    try {
      const nextOwned = new Set(owned);
      nextOwned.add(wantedStickerId);

      const nextDuplicates = [...duplicates];
      const idx = nextDuplicates.indexOf(offeredStickerId);
      if (idx > -1) {
        nextDuplicates.splice(idx, 1);
      }

      await deleteDoc(doc(db, 'trades', id));
      await onUpdateInventory(nextOwned, nextDuplicates);

      setTradeMessage({
        text: `Collected ${STICKER_MAP[wantedStickerId]?.name}! Swapped with ${partnerName}!`,
        type: 'success'
      });
    } catch (err) {
      console.error("Completed trade collection failed:", err);
      setTradeMessage({ text: "Could not finalize. Sync error.", type: 'err' });
    } finally {
      setTradeActionLoading(null);
    }
  };

  return (
      <div className="max-w-6xl mx-auto px-4 py-8 select-none">
        {/* Back to Album Navigation */}
        <button
            onClick={onBack}
            className="inline-flex items-center gap-2 text-xs font-mono text-slate-400 hover:text-white mb-6 uppercase tracking-wider transition-colors cursor-pointer group"
        >
          <ArrowLeft size={14} className="group-hover:-translate-x-1 transition-transform" />
          {t('backToAlbum')}
        </button>

        {/* Tab Navigation header */}
        <div className="flex border-b border-slate-800 mb-7 space-x-6">
          <button
              onClick={() => setActiveTab('social')}
              className={`pb-3 text-xs font-mono font-bold uppercase tracking-wider transition-all cursor-pointer relative ${
                  activeTab === 'social' ? 'text-blue-400 font-bold' : 'text-slate-500 hover:text-slate-300'
              }`}
          >
            {t('socialCoordinators')} 👥
            {activeTab === 'social' && (
                <motion.div layoutId="friendsActiveTab" className="absolute bottom-0 left-0 right-0 h-[2px] bg-blue-500" />
            )}
          </button>
          <button
              onClick={() => setActiveTab('trade')}
              className={`pb-3 text-xs font-mono font-bold uppercase tracking-wider transition-all cursor-pointer relative flex items-center gap-1.5 ${
                  activeTab === 'trade' ? 'text-amber-400 font-bold' : 'text-slate-500 hover:text-slate-300'
              }`}
          >
            {t('tradeOffersHeadline')} 🤝
            {trades.some(t => t.userId === myUid && t.status === 'completed') && (
                <span className="w-2 h-2 rounded-full bg-red-400 animate-pulse shrink-0" />
            )}
            {activeTab === 'trade' && (
                <motion.div layoutId="friendsActiveTab" className="absolute bottom-0 left-0 right-0 h-[2px] bg-amber-500" />
            )}
          </button>
        </div>

        {activeTab === 'social' ? (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

              {/* Left Hand: Connection Search & Requests */}
              <div className="space-y-6">

                {/* Search Card */}
                <div className="bg-[#1e293b] border border-slate-800 rounded-xl p-5 shadow-xl">
                  <div className="flex items-center gap-2 mb-3">
                    <Search size={16} className="text-blue-500" />
                    <h3 className="font-mono text-xs font-bold uppercase tracking-wider text-slate-200">Connect with Collectors</h3>
                  </div>

                  <form onSubmit={handleSearch} className="space-y-3">
                    <div className="relative">
                      <input
                          type="text"
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          placeholder="E.g., email or nickname..."
                          className="w-full bg-slate-950 border border-slate-800 focus:border-blue-500 rounded px-3 py-2 text-xs text-white placeholder-slate-600 outline-none transition-colors pr-8 font-mono"
                      />
                      <button
                          type="submit"
                          disabled={searchLoading}
                          className="absolute right-2.5 top-2 text-slate-500 hover:text-white transition-colors"
                      >
                        {searchLoading ? <RefreshCw size={13} className="animate-spin text-blue-500" /> : <Search size={13} />}
                      </button>
                    </div>
                  </form>

                  {/* Manual Search Results tray */}
                  <AnimatePresence>
                    {(searchResults.length > 0 || searchMessage) && (
                        <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            className="mt-4 border-t border-slate-800/60 pt-3 space-y-2"
                        >
                          {searchMessage && (
                              <p className="text-[10px] font-mono text-slate-400 bg-slate-950/40 p-2.5 rounded border border-slate-900 leading-relaxed">
                                {searchMessage}
                              </p>
                          )}

                          {searchResults.map((res) => {
                            const cState = getConnectionState(res.userId);
                            return (
                                <div
                                    key={res.userId}
                                    className="flex items-center justify-between bg-slate-950/40 p-2.5 rounded border border-slate-900 text-xs"
                                >
                                  <div className="flex items-center gap-2">
                                    <Avatar photoURL={res.photoURL} email={res.email} size="sm" />
                                    <div className="flex flex-col min-w-0">
                            <span className="font-bold text-slate-200 truncate max-w-[120px]">
                              {res.displayName || res.email.split('@')[0]}
                            </span>
                                      <span className="text-[8px] font-mono text-slate-500 max-w-[120px] truncate leading-none">
                              {res.email}
                            </span>
                                    </div>
                                  </div>

                                  {/* Direct Request trigger */}
                                  {cState === 'none' && (
                                      <button
                                          onClick={() => sendFriendRequest(res)}
                                          className="p-1.5 bg-blue-600 hover:bg-blue-500 text-white rounded text-[10px] font-mono font-bold uppercase cursor-pointer"
                                          title="Send Friend Invite"
                                      >
                                        <UserPlus size={11} />
                                      </button>
                                  )}
                                  {cState === 'sent_pending' && (
                                      <span className="text-[8px] font-mono text-yellow-500 bg-yellow-500/10 border border-yellow-500/20 px-1.5 py-0.5 rounded uppercase font-bold">
                            Sent
                          </span>
                                  )}
                                  {cState === 'received_pending' && (
                                      <div className="flex gap-1.5">
                                        <button
                                            onClick={() => acceptFriendRequest(getFriendshipObject(res.userId)!)}
                                            className="p-1 bg-emerald-600 hover:bg-emerald-500 text-white rounded cursor-pointer"
                                            title="Accept"
                                        >
                                          <Check size={11} />
                                        </button>
                                      </div>
                                  )}
                                  {cState === 'accepted' && (
                                      <span className="text-[8px] font-mono text-emerald-400 bg-emerald-400/10 border border-emerald-400/20 px-1.5 py-0.5 rounded uppercase font-bold">
                            Friends
                          </span>
                                  )}
                                </div>
                            );
                          })}
                        </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Inbound Friend Requests List */}
                <div className="bg-[#1e293b] border border-slate-800 rounded-xl p-5 shadow-xl">
                  <div className="flex items-center gap-2 mb-3">
                    <UserCheck size={16} className="text-yellow-500" />
                    <h3 className="font-mono text-xs font-bold uppercase tracking-wider text-slate-200">
                      Inbound Invites ({inboundPending.length})
                    </h3>
                  </div>

                  {inboundPending.length === 0 ? (
                      <p className="text-[10px] font-mono text-slate-500 text-center py-4 bg-slate-950/20 border border-slate-900 border-dashed rounded font-bold">
                        No Pending Inbound Requests
                      </p>
                  ) : (
                      <div className="space-y-2">
                        {inboundPending.map((req) => {
                          const requester = friendProfiles[req.senderId] || { userId: req.senderId, email: 'Loading...', displayName: 'Loading...' };
                          return (
                              <div
                                  key={req.id}
                                  className="flex items-center justify-between p-2.5 bg-slate-950/40 border border-slate-900 rounded text-xs"
                              >
                                <div className="flex items-center gap-2 min-w-0">
                                  <Avatar photoURL={requester.photoURL} email={requester.email} size="sm" />
                                  <div className="flex flex-col min-w-0">
                          <span className="font-bold text-slate-200 truncate max-w-[120px]">
                            {requester.displayName || requester.email.split('@')[0]}
                          </span>
                                    <span className="text-[8px] font-mono text-slate-500 truncate leading-none">
                            {requester.email}
                          </span>
                                  </div>
                                </div>

                                <div className="flex items-center gap-1.5">
                                  <button
                                      onClick={() => acceptFriendRequest(req)}
                                      className="p-1 bg-emerald-600 hover:bg-emerald-500 text-white rounded font-mono font-bold text-[9px] cursor-pointer flex items-center gap-0.5 px-2"
                                  >
                                    <Check size={11} /> Accept
                                  </button>
                                  <button
                                      onClick={() => terminateFriendship(req.id)}
                                      className="p-1 bg-slate-800 hover:bg-slate-700 text-slate-400 rounded cursor-pointer"
                                      title="Ignore request"
                                  >
                                    <X size={11} />
                                  </button>
                                </div>
                              </div>
                          );
                        })}
                      </div>
                  )}
                </div>

                {/* Outbound Friend Requests List */}
                <div className="bg-[#1e293b] border border-slate-800 rounded-xl p-5 shadow-xl">
                  <div className="flex items-center gap-2 mb-3">
                    <Clock size={16} className="text-slate-400" />
                    <h3 className="font-mono text-xs font-bold uppercase tracking-wider text-slate-200">
                      Outbound Invites ({outboundPending.length})
                    </h3>
                  </div>

                  {outboundPending.length === 0 ? (
                      <p className="text-[10px] font-mono text-slate-500 text-center py-2 bg-slate-950/20 border border-slate-900 border-dashed rounded">
                        No outbound invites pending response
                      </p>
                  ) : (
                      <div className="space-y-2">
                        {outboundPending.map((req) => {
                          const receiver = friendProfiles[req.receiverId] || { userId: req.receiverId, email: 'Loading...', displayName: 'Loading...' };
                          return (
                              <div
                                  key={req.id}
                                  className="flex items-center justify-between p-2.5 bg-slate-950/40 border border-slate-900 rounded text-xs leading-none"
                              >
                                <div className="flex items-center gap-2 min-w-0">
                                  <Avatar photoURL={receiver.photoURL} email={receiver.email} size="sm" />
                                  <div className="flex flex-col min-w-0">
                          <span className="font-bold text-slate-200 truncate max-w-[120px]">
                            {receiver.displayName || receiver.email.split('@')[0]}
                          </span>
                                    <span className="text-[8px] font-mono text-slate-500 truncate mt-0.5">
                            {receiver.email}
                          </span>
                                  </div>
                                </div>

                                <button
                                    onClick={() => terminateFriendship(req.id)}
                                    className="p-1 text-slate-500 hover:text-red-400 transition-colors uppercase font-mono text-[8px] font-bold border border-slate-800 hover:border-red-500/20 rounded hover:bg-red-500/5 cursor-pointer"
                                >
                                  Cancel
                                </button>
                              </div>
                          );
                        })}
                      </div>
                  )}
                </div>

              </div>

              {/* Right Area: List of Friends & Trades Comparative Summary */}
              <div className="lg:col-span-2 space-y-6">

                {/* Compare Album Modal/Subpanel Overlay */}
                <AnimatePresence>
                  {selectedFriendCompare && (
                      <motion.div
                          initial={{ opacity: 0, y: 15 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -15 }}
                          className="bg-[#1e293b] border border-blue-500/30 rounded-xl p-6 shadow-xl relative overflow-hidden"
                      >
                        {/* Visual decoration line */}
                        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500 to-amber-500" />

                        <button
                            onClick={() => setSelectedFriendCompare(null)}
                            className="absolute top-4 right-4 text-slate-500 hover:text-white bg-slate-950/40 hover:bg-slate-900 p-1.5 rounded-full transition-all cursor-pointer"
                        >
                          <X size={14} />
                        </button>

                        <div className="flex items-center gap-3 mb-5">
                          <Avatar photoURL={selectedFriendCompare.photoURL} email={selectedFriendCompare.email} size="lg" />
                          <div className="space-y-0.5">
                            <div className="flex items-center gap-1.5">
                              <h3 className="font-impact text-lg text-white uppercase tracking-wide">
                                Comparing Album with {selectedFriendCompare.displayName || selectedFriendCompare.email.split('@')[0]}
                              </h3>
                              <span className="px-1.5 py-0.5 bg-blue-500/10 text-blue-400 border border-blue-500/20 rounded text-[8px] font-mono md:inline hidden">
                        LIVE COMPARISON
                      </span>
                            </div>
                            <p className="text-[9px] font-mono text-slate-500 uppercase">{selectedFriendCompare.email}</p>
                          </div>
                        </div>

                        {/* Compare Navigation Tabs */}
                        <div className="flex border-b border-slate-800 font-mono text-xs gap-1.5 mb-5">
                          <button
                              onClick={() => setCompareTab('summary')}
                              className={`pb-2 px-3 tracking-wider uppercase font-bold text-[10px] transition-all cursor-pointer ${
                                  compareTab === 'summary' ? 'border-b-2 border-blue-500 text-blue-400' : 'text-slate-400 hover:text-white'
                              }`}
                          >
                            Summary Matchup
                          </button>
                          <button
                              onClick={() => setCompareTab('get')}
                              className={`pb-2 px-3 tracking-wider uppercase font-bold text-[10px] transition-all cursor-pointer ${
                                  compareTab === 'get' ? 'border-b-2 border-blue-500 text-blue-400' : 'text-slate-400 hover:text-white'
                              }`}
                          >
                            Stickers You Need
                          </button>
                          <button
                              onClick={() => setCompareTab('give')}
                              className={`pb-2 px-3 tracking-wider uppercase font-bold text-[10px] transition-all cursor-pointer ${
                                  compareTab === 'give' ? 'border-b-2 border-blue-500 text-blue-400' : 'text-slate-400 hover:text-white'
                              }`}
                          >
                            Duplicates You Can Give
                          </button>
                        </div>

                        {/* Tab 1: Summary matchup progress */}
                        {compareTab === 'summary' && (
                            <div className="space-y-5">
                              <p className="text-[10px] font-mono text-slate-400 leading-relaxed bg-slate-950/30 p-3 rounded-lg border border-slate-900">
                                View a side-by-side progression analysis of your World Cup sticker manifests. You can analyze duplicate items to arrange direct external trades!
                              </p>

                              <div className="grid grid-cols-2 gap-4">
                                {/* My Stats Box */}
                                <div className="bg-slate-950/40 border border-slate-900 p-4 rounded-xl text-center space-y-1">
                                  <div className="text-[10px] font-mono text-slate-500 uppercase font-black">Your Standing</div>
                                  <div className="font-impact text-3xl text-blue-500">{owned.size} <span className="text-slate-600 text-xs">/ {ALL_STICKERS.length}</span></div>
                                  <div className="text-[10px] font-mono text-white/90 uppercase bg-blue-500/10 border border-blue-500/20 py-1 max-w-[100px] mx-auto rounded-full font-bold">
                                    {Math.round((owned.size / ALL_STICKERS.length) * 100)}% Complete
                                  </div>
                                </div>

                                {/* Friend Stats Box */}
                                <div className="bg-slate-950/40 border border-slate-900 p-4 rounded-xl text-center space-y-1">
                                  <div className="text-[10px] font-mono text-slate-500 uppercase font-black">
                                    {selectedFriendCompare.displayName || 'Collector'}'s Standing
                                  </div>
                                  <div className="font-impact text-3xl text-amber-500">
                                    {selectedFriendCompare.owned?.length || 0} <span className="text-slate-600 text-xs">/ {ALL_STICKERS.length}</span>
                                  </div>
                                  <div className="text-[10px] font-mono text-white/90 uppercase bg-amber-500/10 border border-amber-500/20 py-1 max-w-[100px] mx-auto rounded-full font-bold">
                                    {Math.round(((selectedFriendCompare.owned?.length || 0) / ALL_STICKERS.length) * 100)}% Complete
                                  </div>
                                </div>
                              </div>

                              {/* Detailed progress bar comparisons */}
                              <div className="space-y-3.5 bg-slate-950/20 border border-slate-800 p-4 rounded-lg">
                                <div className="space-y-1">
                                  <div className="flex justify-between items-center text-[9px] font-mono text-slate-400 uppercase font-bold">
                                    <span>Your Album Completion</span>
                                    <span>{owned.size} Stickers</span>
                                  </div>
                                  <div className="w-full bg-slate-950 h-2.5 rounded border border-slate-900 overflow-hidden">
                                    <div className="bg-blue-500 h-full transition-all" style={{ width: `${Math.round((owned.size / ALL_STICKERS.length) * 100)}%` }} />
                                  </div>
                                </div>

                                <div className="space-y-1">
                                  <div className="flex justify-between items-center text-[9px] font-mono text-slate-400 uppercase font-bold">
                                    <span>their Album Completion</span>
                                    <span>{selectedFriendCompare.owned?.length || 0} Stickers</span>
                                  </div>
                                  <div className="w-full bg-slate-950 h-2.5 rounded border border-slate-900 overflow-hidden">
                                    <div className="bg-amber-500 h-full transition-all" style={{ width: `${Math.round(((selectedFriendCompare.owned?.length || 0) / ALL_STICKERS.length) * 100)}%` }} />
                                  </div>
                                </div>
                              </div>
                            </div>
                        )}

                        {/* Tab 2: Stickers they have duplicates of that we are missing */}
                        {compareTab === 'get' && (() => {
                          const trades = getTradingMatrix(selectedFriendCompare);
                          return (
                              <div className="space-y-4">
                                <p className="text-[10px] font-mono text-slate-400 leading-relaxed bg-slate-950/20 p-3 rounded-lg">
                                  These are stickers {selectedFriendCompare.displayName || 'your friend'} has **Duplicate copies** of inside their spares vault, which you do not have in your album. Arrange a swap to gather these!
                                </p>

                                {trades.youNeed.length === 0 ? (
                                    <div className="py-12 bg-slate-950/20 border border-slate-850 rounded-lg flex flex-col items-center justify-center text-center p-4">
                                      <Award size={20} className="text-slate-600 mb-2" />
                                      <span className="text-[10px] font-mono font-bold text-slate-500 uppercase">No immediately tradeable spares</span>
                                      <span className="text-[9px] font-mono text-slate-600 mt-0.5">They don't have duplicates of any cards you are currently missing.</span>
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 max-h-[250px] overflow-y-auto pr-1">
                                      {trades.youNeed.map((sticker) => {
                                        if (!sticker) return null;
                                        const rarityColor = sticker.rarity === 'legendary' ? 'border-amber-500 text-amber-400' : sticker.rarity === 'rare' ? 'border-emerald-500 text-emerald-400' : 'border-slate-800 text-slate-300';
                                        return (
                                            <div
                                                key={sticker.id}
                                                className={`p-2 bg-slate-950/50 border rounded text-center flex flex-col justify-between items-center min-h-[75px] ${rarityColor}`}
                                            >
                                              <div className="text-base mb-1">{sticker.type === 'special' ? sticker.icon : '⚽'}</div>
                                              <span className="text-[9px] font-mono font-bold text-white uppercase max-w-full truncate leading-tight">{sticker.name}</span>
                                              <span className="text-[7px] font-mono text-slate-500 uppercase">{sticker.rarity}</span>
                                            </div>
                                        );
                                      })}
                                    </div>
                                )}
                              </div>
                          );
                        })()}

                        {/* Tab 3: Stickers I have duplicates of that they are missing */}
                        {compareTab === 'give' && (() => {
                          const trades = getTradingMatrix(selectedFriendCompare);
                          return (
                              <div className="space-y-4">
                                <p className="text-[10px] font-mono text-slate-400 leading-relaxed bg-slate-950/20 p-3 rounded-lg">
                                  These are stickers from your spares selection that {selectedFriendCompare.displayName || 'your friend'} is currently missing from their World Cup series collection. Offer these to help them complete their catalog!
                                </p>

                                {trades.friendNeeds.length === 0 ? (
                                    <div className="py-12 bg-slate-950/20 border border-slate-850 rounded-lg flex flex-col items-center justify-center text-center p-4">
                                      <Award size={20} className="text-slate-600 mb-2" />
                                      <span className="text-[10px] font-mono font-bold text-slate-500 uppercase">No trade gifts available</span>
                                      <span className="text-[9px] font-mono text-slate-600 mt-0.5">None of your duplicates are currently needed for their outstanding slots.</span>
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 max-h-[250px] overflow-y-auto pr-1">
                                      {trades.friendNeeds.map((sticker) => {
                                        if (!sticker) return null;
                                        const rarityColor = sticker.rarity === 'legendary' ? 'border-amber-500 text-amber-400' : sticker.rarity === 'rare' ? 'border-emerald-500 text-emerald-400' : 'border-slate-800 text-slate-300';
                                        return (
                                            <div
                                                key={sticker.id}
                                                className={`p-2 bg-slate-950/50 border rounded text-center flex flex-col justify-between items-center min-h-[75px] ${rarityColor}`}
                                            >
                                              <div className="text-base mb-1">{sticker.type === 'special' ? sticker.icon : '⚽'}</div>
                                              <span className="text-[9px] font-mono font-bold text-white uppercase max-w-full truncate leading-tight">{sticker.name}</span>
                                              <span className="text-[7px] font-mono text-slate-500 uppercase">{sticker.rarity}</span>
                                            </div>
                                        );
                                      })}
                                    </div>
                                )}
                              </div>
                          );
                        })()}

                      </motion.div>
                  )}
                </AnimatePresence>

                {/* Connected Friends List */}
                <div className="bg-[#1e293b] border border-slate-800 rounded-xl p-6 shadow-xl space-y-4">
                  <div className="flex items-center justify-between border-b border-slate-800/80 pb-3">
                    <div className="flex items-center gap-2">
                      <Users size={18} className="text-blue-500 animate-pulse" />
                      <h3 className="font-impact text-xl text-white uppercase tracking-wider">Accepted Collector Connections</h3>
                    </div>
                    <span className="text-[9px] font-mono uppercase bg-blue-500/10 text-blue-400 border border-blue-500/20 px-2 py-0.5 rounded-full font-bold">
                {acceptedFriends.length} Connected
              </span>
                  </div>

                  {acceptedFriends.length === 0 ? (
                      <div className="py-12 bg-slate-950/20 border border-slate-800 border-dashed rounded-lg flex flex-col items-center justify-center text-center p-6">
                        <Award size={30} className="text-slate-600 mb-3" />
                        <h4 className="font-mono text-xs font-bold uppercase tracking-wider text-slate-400">Your Connection manifesting is empty!</h4>
                        <p className="text-[10px] text-slate-500 mt-1 max-w-[350px] leading-relaxed">
                          Search other active collectors on the left by exact email, or find people from the active player pool directory below to send your first friend invite!
                        </p>
                      </div>
                  ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {acceptedFriends.map((friend) => {
                          const fCount = friend.owned?.length || 0;
                          const fPct = Math.round((fCount / ALL_STICKERS.length) * 100);
                          const fDupes = friend.duplicates?.length || 0;
                          const friendship = getFriendshipObject(friend.userId)!;

                          return (
                              <div
                                  key={friend.userId}
                                  className="bg-slate-950/40 border border-slate-900 hover:border-blue-500/20 rounded-xl p-4 transition-all flex flex-col justify-between space-y-4 relative group hover:shadow-lg"
                              >
                                <div className="flex items-start justify-between min-w-0">
                                  <div className="flex items-center gap-3 min-w-0">
                                    <Avatar photoURL={friend.photoURL} email={friend.email} size="md" />
                                    <div className="flex flex-col min-w-0 leading-tight">
                            <span className="font-bold text-white text-xs truncate max-w-[140px]">
                              {friend.displayName || friend.email.split('@')[0]}
                            </span>
                                      <span className="text-[8px] font-mono text-slate-500 truncate max-w-[140px] mt-0.5">
                              {friend.email}
                            </span>
                                    </div>
                                  </div>

                                  {/* Interactive Compare Action */}
                                  <button
                                      onClick={() => {
                                        setSelectedFriendCompare(friend);
                                        setCompareTab('summary');
                                      }}
                                      className="px-2.5 py-1 bg-blue-600 hover:bg-blue-500 text-white font-mono font-bold text-[8px] uppercase rounded transition-all flex items-center gap-1 cursor-pointer"
                                  >
                                    <ArrowUpDown size={10} /> Compare Album
                                  </button>
                                </div>

                                {/* Stat summary */}
                                <div className="grid grid-cols-2 gap-2 text-center text-[10px] font-mono leading-none border-t border-slate-900/40 pt-3">
                                  <div className="bg-slate-950/60 p-2 rounded">
                                    <span className="text-[7px] text-slate-500 block uppercase mb-1">Album Completion</span>
                                    <span className="text-white font-bold">{fPct}% ({fCount} / {ALL_STICKERS.length})</span>
                                  </div>
                                  <div className="bg-slate-950/60 p-2 rounded">
                                    <span className="text-[7px] text-slate-500 block uppercase mb-1">Spares Stock</span>
                                    <span className="text-emerald-400 font-bold">+{fDupes} spares</span>
                                  </div>
                                </div>

                                {/* Destructive Remove connection */}
                                <div className="flex justify-end pt-1">
                                  <button
                                      onClick={() => terminateFriendship(friendship.id)}
                                      className="text-[8px] font-mono font-bold uppercase tracking-tight text-slate-600 hover:text-red-400 transition-colors flex items-center gap-1 opacity-0 group-hover:opacity-100 cursor-pointer"
                                      title="Unfriend Collector"
                                  >
                                    <Trash2 size={10} /> Disconnect
                                  </button>
                                </div>
                              </div>
                          );
                        })}
                      </div>
                  )}
                </div>

                {/* Active Player Pool Directory */}
                <div className="bg-[#1e293b]/70 border border-slate-800 rounded-xl p-5 shadow-xl space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Sparkles size={15} className="text-amber-400" />
                      <h4 className="font-mono text-xs font-bold uppercase tracking-wider text-slate-300">
                        Registered Collectors Directory
                      </h4>
                    </div>
                    <button
                        onClick={loadCommunityDirectory}
                        className="text-[9px] font-mono text-slate-400 hover:text-white uppercase flex items-center gap-1.5 font-bold cursor-pointer"
                    >
                      <RefreshCw size={10} className={loadingDirectory ? 'animate-spin text-blue-400' : ''} /> Refresh Directory
                    </button>
                  </div>

                  <p className="text-[9px] font-mono text-slate-400 leading-normal max-w-xl">
                    This pool displays active collectors who marked themselves as open to swap and connect. Activating your own discovery beacon in your User Profile page lists you prominently on this directory with a live active icon!
                  </p>

                  {loadingDirectory ? (
                      <div className="flex justify-center py-6">
                        <RefreshCw size={16} className="animate-spin text-blue-500" />
                      </div>
                  ) : directoryUsers.length === 0 ? (
                      <p className="text-[9px] font-mono text-slate-500 text-center py-4">No other active collectors found in the database directory.</p>
                  ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
                        {directoryUsers.map((item) => {
                          const stateStatus = getConnectionState(item.userId);
                          const fCount = item.owned?.length || 0;
                          const fPct = Math.round((fCount / ALL_STICKERS.length) * 100);

                          return (
                              <div
                                  key={item.userId}
                                  className="p-2.5 bg-slate-950/30 border border-slate-900 rounded-lg flex items-center justify-between text-xs"
                              >
                                <div className="flex items-center gap-2 min-w-0">
                                  <Avatar photoURL={item.photoURL} email={item.email} size="sm" />
                                  <div className="flex flex-col min-w-0 leading-tight">
                                    <div className="flex items-center gap-1.5">
                            <span className="font-bold text-slate-300 truncate max-w-[100px]" title={item.displayName || item.email}>
                              {item.displayName || item.email.split('@')[0]}
                            </span>
                                      {item.connectOpen && (
                                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-ping shrink-0" title="Connect Open Active" />
                                      )}
                                    </div>
                                    <div className="flex items-center gap-1.5 mt-0.5">
                            <span className="text-[7px] font-mono text-slate-500 uppercase">
                              {fPct}% Completeness
                            </span>
                                      {item.connectOpen && (
                                          <span className="text-[6px] font-mono text-emerald-400 bg-emerald-500/10 px-1 rounded border border-emerald-500/20 uppercase scale-90 tracking-tighter">
                                Beacon Open
                              </span>
                                      )}
                                    </div>
                                  </div>
                                </div>

                                {/* Social Actions button */}
                                {stateStatus === 'none' && (
                                    <button
                                        onClick={() => sendFriendRequest(item)}
                                        className="flex items-center gap-1.5 bg-slate-800 hover:bg-slate-700 hover:text-white py-1 px-2 border border-slate-700 rounded text-[9px] font-mono text-slate-300 font-bold uppercase transition-all cursor-pointer"
                                    >
                                      <Plus size={9} /> Add Friend
                                    </button>
                                )}
                                {stateStatus === 'sent_pending' && (
                                    <span className="text-[7px] font-mono text-yellow-500 bg-yellow-500/10 border border-yellow-500/30 px-2 py-0.5 rounded-full uppercase font-bold">
                          Pending
                        </span>
                                )}
                                {stateStatus === 'received_pending' && (
                                    <button
                                        onClick={() => acceptFriendRequest(getFriendshipObject(item.userId)!)}
                                        className="bg-emerald-600 hover:bg-emerald-500 py-1 px-2 rounded text-[9px] text-white font-mono font-bold uppercase cursor-pointer"
                                    >
                                      Accept
                                    </button>
                                )}
                                {stateStatus === 'accepted' && (
                                    <span className="text-[7px] font-mono text-emerald-400 bg-emerald-400/10 border border-emerald-400/30 px-2 py-0.5 rounded-full uppercase font-bold">
                          Connected
                        </span>
                                )}
                              </div>
                          );
                        })}
                      </div>
                  )}
                </div>

              </div>

            </div>
        ) : (
            <TradeBoard
                user={user}
                owned={owned}
                duplicates={duplicates}
                acceptedFriends={acceptedFriends}
                trades={trades}
                onUpdateInventory={onUpdateInventory}
                db={db}
            />
        )}
      </div>
  );
};
